import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { Play, Mail, ArrowLeft, CheckCircle2, KeyRound } from "lucide-react";

import { API_URL } from "../config/api";

const API = API_URL;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetting, setResetting] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      // Token is returned directly - no email needed!
      if (response.data.reset_token) {
        setResetToken(response.data.reset_token);
        toast.success("Reset code gegenereerd!");
      } else {
        setResetToken("");
        toast.success("Reset code aangevraagd");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Dit email adres is niet bekend";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("Wachtwoorden komen niet overeen");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("Wachtwoord moet minimaal 6 tekens zijn");
      return;
    }

    setResetting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email,
        reset_token: resetToken,
        new_password: newPassword
      });
      toast.success("Wachtwoord succesvol gewijzigd! Je kunt nu inloggen.");
      // Redirect to login after 2 seconds
      setTimeout(() => {
        window.location.href = "/login";
      }, 2000);
    } catch (error) {
      const message = error.response?.data?.detail || "Kon wachtwoord niet resetten";
      toast.error(message);
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] light-mode flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        {/* Back link */}
        <Link to="/login" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Terug naar inloggen
        </Link>

        {/* Logo */}
        <div className="flex items-center gap-2 mb-8">
          <div className="w-12 h-12 rounded-xl bg-indigo-600 flex items-center justify-center">
            <Play className="w-6 h-6 text-white fill-white" />
          </div>
          <span className="font-bold text-2xl text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>VideoMaker</span>
        </div>

        <Card className="border-slate-200 shadow-xl">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {sent ? "Wachtwoord Resetten" : "Wachtwoord Vergeten"}
            </CardTitle>
            <CardDescription className="text-slate-500">
              {sent 
                ? "Voer je nieuwe wachtwoord in"
                : "Vul je email in om je wachtwoord te resetten"
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!sent ? (
              <form onSubmit={handleRequestReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      data-testid="forgot-email-input"
                      placeholder="naam@bedrijf.nl"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-12 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  data-testid="forgot-submit-btn"
                  className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-all duration-300 active:scale-[0.98]"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Versturen...
                    </div>
                  ) : (
                    "Verstuur Reset Link"
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 text-emerald-700">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="font-medium">Reset code gegenereerd!</span>
                  </div>
                  <p className="text-sm text-emerald-600 mt-2">
                    Gebruik de code hieronder om je wachtwoord te wijzigen voor <strong>{email}</strong>.
                    <br/>De code is 15 minuten geldig.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="token" className="text-slate-700">Reset Code</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="token"
                      type="text"
                      data-testid="reset-token-input"
                      placeholder="6-cijferige code"
                      value={resetToken}
                      onChange={(e) => setResetToken(e.target.value)}
                      className="pl-10 h-12 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500 text-center text-xl tracking-widest font-mono bg-emerald-50"
                      maxLength={6}
                      required
                      readOnly={resetToken.length === 6}
                    />
                  </div>
                  {resetToken.length === 6 && (
                    <p className="text-xs text-emerald-600">✓ Code automatisch ingevuld</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-slate-700">Nieuw Wachtwoord</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    data-testid="new-password-input"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="h-12 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500"
                    required
                    minLength={6}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-slate-700">Bevestig Wachtwoord</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    data-testid="confirm-password-input"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="h-12 border-slate-200 focus:border-indigo-500 focus:ring-indigo-500"
                    required
                    minLength={6}
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="reset-submit-btn"
                  className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-all duration-300 active:scale-[0.98]"
                  disabled={resetting}
                >
                  {resetting ? (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Resetten...
                    </div>
                  ) : (
                    "Wachtwoord Wijzigen"
                  )}
                </Button>

                <button
                  type="button"
                  onClick={() => setSent(false)}
                  className="w-full text-center text-slate-600 hover:text-slate-900 text-sm mt-2"
                >
                  Andere email gebruiken
                </button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
